import {
  AdvancePermitStageRequest,
  Inspection,
  type InspectionStatusList,
  Permit,
  PermitGroup,
  type PermitList,
  type PermitStatus,
  type PermitStatusList,
  PermitTrade,
  type PermitTradeList,
} from '@kalos/kalos-rpc';
import { RpcError } from '@protobuf-ts/runtime-rpc';
import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { mergePermitsForJob } from '../../modules/Permits/utils';
import { PermitClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

/**
 * After any Permitting write: permits, permit requests, inspections and Activity read
 * fresh from the server. Every Permitting mutation refreshes rather than editing cache
 * shapes by hand, so a list, a single permit and its Activity never show stale data
 * after a save or after going back.
 */
export const refreshPermitting = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: [queryKeys.permits.root] }),
    queryClient.invalidateQueries({ queryKey: [queryKeys.permitGroups.root] }),
    queryClient.invalidateQueries({ queryKey: [queryKeys.inspections.root] }),
    queryClient.invalidateQueries({ queryKey: [queryKeys.permitActivity.root] }),
  ]);

export type PermitGroupFilter = EntityFilter<PermitGroup>;
export const usePermitGroupsBatchGetQuery = ({
  filter = {},
  keepPreviousResults = false,
}: {
  filter?: PermitGroupFilter;
  /** Keep showing the last results while a new filter or sort loads (no skeleton). */
  keepPreviousResults?: boolean;
} = {}) => {
  return useQuery({
    placeholderData: keepPreviousResults ? keepPreviousData : undefined,
    queryFn: async () => {
      return await PermitClientService.BatchGetPermitGroup(
        PermitGroup.create({
          ...filter,
          isActive: true,
        }),
      );
    },
    queryKey: [
      queryKeys.permitGroups.root,
      queryKeys.permitGroups.list,
      queryKeys.permitGroups.getHash(filter),
    ],
  });
};

export const usePermitGroupQuery = ({
  filter = {},
  enabled,
}: { filter?: PermitGroupFilter; enabled?: boolean } = {}) => {
  return useQuery({
    queryFn: async () => {
      return await PermitClientService.GetPermitGroup(
        PermitGroup.create({
          ...filter,
          isActive: true,
        }),
      );
    },
    queryKey: [queryKeys.permitGroups.root, queryKeys.permitGroups.getHash(filter)],
    enabled,
    retry: false,
  });
};

export const useCreatePermitGroupMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (permitGroup: PermitGroup) => {
      const createdPermitGroup = await PermitClientService.CreatePermitGroup(permitGroup);
      return createdPermitGroup;
    },
    onSuccess: () => refreshPermitting(queryClient),
  });
};

export const useUpdatePermitGroupMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (permitGroup: PermitGroup) => {
      return await PermitClientService.UpdatePermitGroup(permitGroup);
    },
    onSuccess: () => refreshPermitting(queryClient),
  });
};

export type PermitFilter = EntityFilter<Permit>;
export const usePermitsBatchGetQuery = <TSelectData = PermitList,>({
  filter = {},
  enabled,
  select,
}: {
  filter?: PermitFilter;
  enabled?: boolean;
  select?: (data: PermitList) => TSelectData;
} = {}) => {
  return useQuery({
    queryFn: async () => {
      return await PermitClientService.BatchGetPermit(Permit.create(filter));
    },
    queryKey: [queryKeys.permits.root, queryKeys.permits.list, queryKeys.permits.getHash(filter)],
    enabled,
    select,
  });
};

// Also finds permits stored with job 0 whose group has this job (see mergePermitsForJob).
export const usePermitsByJobQuery = <TSelectData = Permit[],>({
  jobNumber,
  enabled = true,
  select,
}: {
  jobNumber: number;
  enabled?: boolean;
  select?: (data: Permit[]) => TSelectData;
}) => {
  return useQuery({
    queryFn: async () => {
      const [directMatches, groupsForJob] = await Promise.all([
        PermitClientService.BatchGetPermit(Permit.create({ jobNumber, withoutLimit: true })),
        PermitClientService.BatchGetPermitGroup(
          PermitGroup.create({ po: jobNumber.toString(), isActive: true, withoutLimit: true }),
        ),
      ]);
      return mergePermitsForJob(directMatches.results, groupsForJob.results);
    },
    queryKey: [queryKeys.permits.root, queryKeys.permits.list, 'byJob', jobNumber],
    enabled: enabled && jobNumber > 0,
    select,
  });
};

/**
 * The active permit requests on some jobs: those whose Job # is one of them, plus the
 * requests of active permits added on one of them from another job's request. A few
 * reads per job, for the job page and a project's Permits tab, never the whole list.
 */
export const usePermitGroupsForJobsQuery = (jobNumbers: number[]) => {
  const jobs = [...new Set(jobNumbers.filter((job) => job > 0))].sort((a, b) => a - b);
  return useQuery({
    queryFn: async () => {
      const perJob = await Promise.all(
        jobs.map((jobNumber) =>
          Promise.all([
            PermitClientService.BatchGetPermitGroup(
              PermitGroup.create({ po: String(jobNumber), isActive: true, withoutLimit: true }),
            ),
            PermitClientService.BatchGetPermit(
              Permit.create({ jobNumber, isActive: true, withoutLimit: true }),
            ),
          ]),
        ),
      );
      const groups = new Map<number, PermitGroup>();
      for (const [byJob] of perJob) for (const group of byJob.results) groups.set(group.id, group);
      const otherGroupIds = new Set(
        perJob
          .flatMap(([, permits]) => permits.results)
          .map((permit) => permit.permitGroupId)
          .filter((id) => id > 0 && !groups.has(id)),
      );
      // A request that was deleted (inactive) isn't found; its permits don't show.
      const others = await Promise.all(
        [...otherGroupIds].map((id) =>
          PermitClientService.GetPermitGroup(PermitGroup.create({ id, isActive: true })).catch(
            (error: unknown) => {
              if (error instanceof RpcError && error.code === 'NOT_FOUND') return undefined;
              throw error;
            },
          ),
        ),
      );
      for (const other of others) if (other) groups.set(other.id, other);
      return [...groups.values()];
    },
    queryKey: [queryKeys.permitGroups.root, queryKeys.permitGroups.list, 'forJobs', jobs],
    enabled: jobs.length > 0,
  });
};

const getUsePermitGetQueryConfig = (filter: PermitFilter = {}) => ({
  queryFn: async () => {
    return await PermitClientService.GetPermit(Permit.create(filter));
  },
  queryKey: [queryKeys.permits.root, queryKeys.permits.getHash(filter)],
});

export const useInlinePermitGetQuery = () => {
  const queryClient = useQueryClient();

  const fetchPermit = useCallback(
    async ({ filter = {} }: { filter?: PermitFilter } = {}) => {
      const { queryFn, queryKey } = getUsePermitGetQueryConfig(filter);
      const cache = queryClient.getQueryData<Permit>(queryKey);
      if (!cache) {
        const permit = await queryFn();
        queryClient.setQueryData(queryKey, permit);
        return permit;
      }
      return cache;
    },
    [queryClient],
  );

  return useMemo(
    () => ({
      fetchPermit,
    }),
    [fetchPermit],
  );
};

export const usePermitGetQuery = ({
  filter = {},
  enabled,
}: { filter?: PermitFilter; enabled?: boolean } = {}) => {
  return useQuery({
    ...getUsePermitGetQueryConfig(filter),
    enabled,
  });
};

export type PermitStatusFilter = EntityFilter<PermitStatus>;
export const usePermitStatusBatchGetQuery = <TSelectData = PermitStatusList,>({
  select,
  enabled,
}: { select?: (data: PermitStatusList) => TSelectData; enabled?: boolean } = {}) => {
  return useQuery({
    queryFn: async () => {
      return await PermitClientService.BatchGetPermitStatuses();
    },
    queryKey: [queryKeys.permitStatuses.root, queryKeys.permitStatuses.list],
    select,
    enabled,
  });
};

export type PermitTradeFilter = EntityFilter<PermitTrade>;
export const usePermitTradeBatchGetQuery = <TSelectData = PermitTradeList,>({
  filter = {},
  enabled,
  select,
}: {
  filter?: PermitTradeFilter;
  enabled?: boolean;
  select?: (data: PermitTradeList) => TSelectData;
} = {}) => {
  return useQuery({
    queryFn: async () => {
      return await PermitClientService.BatchGetPermitTrades(PermitTrade.create(filter));
    },
    queryKey: [
      queryKeys.permitTrade.root,
      queryKeys.permitTrade.list,
      queryKeys.permitTrade.getHash(filter),
    ],
    enabled,
    select,
  });
};

export const usePermitCreateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (permit: Permit) => {
      return await PermitClientService.CreatePermit(permit);
    },
    onSuccess: () => refreshPermitting(queryClient),
  });
};

export const usePermitUpdateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (permit: Permit) => {
      return await PermitClientService.UpdatePermit(permit);
    },
    onSuccess: () => refreshPermitting(queryClient),
  });
};

export type InspectionFilter = EntityFilter<Inspection>;
export const useInspectionsBatchGetQuery = ({
  filter = {},
}: { filter?: InspectionFilter } = {}) => {
  return useQuery({
    queryFn: async () => {
      return await PermitClientService.BatchGetInspection(Inspection.create(filter));
    },
    queryKey: [
      queryKeys.inspections.root,
      queryKeys.inspections.list,
      queryKeys.inspections.getHash(filter),
    ],
  });
};

export const useCreateInspectionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inspection: Inspection) => {
      return await PermitClientService.CreateInspection(inspection);
    },
    onSuccess: () => refreshPermitting(queryClient),
  });
};

export const useUpdateInspectionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inspection: Inspection) => {
      return await PermitClientService.UpdateInspection(inspection);
    },
    onSuccess: () => refreshPermitting(queryClient),
  });
};

export const useInspectionStatusesQuery = <TSelectData = InspectionStatusList,>({
  select,
  enabled,
}: { select?: (data: InspectionStatusList) => TSelectData; enabled?: boolean } = {}) => {
  return useQuery({
    queryFn: async () => {
      return await PermitClientService.BatchGetInspectionStatus();
    },
    queryKey: [queryKeys.inspectionStatus.root, queryKeys.inspectionStatus.list],
    select,
    enabled,
  });
};

export const useCreatePermitGroupDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      args: Parameters<typeof PermitClientService.CreatePermitGroupDocument>['0'],
    ) => {
      return await PermitClientService.CreatePermitGroupDocument(args);
    },
    onSuccess: () => refreshPermitting(queryClient),
  });
};

export const useDeletePermitGroupDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      args: Parameters<typeof PermitClientService.DeletePermitGroupDocument>['0'],
    ) => {
      return await PermitClientService.DeletePermitGroupDocument(args);
    },
    onSuccess: () => refreshPermitting(queryClient),
  });
};

export const useCreatePermitDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: Parameters<typeof PermitClientService.CreatePermitDocument>['0']) => {
      return await PermitClientService.CreatePermitDocument(args);
    },
    onSuccess: () => refreshPermitting(queryClient),
  });
};

export const useUpdatePermitDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: Parameters<typeof PermitClientService.UpdatePermitDocument>['0']) =>
      PermitClientService.UpdatePermitDocument(args),
    onSuccess: () => refreshPermitting(queryClient),
  });
};

export const useDeletePermitDocumentMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: Parameters<typeof PermitClientService.DeletePermitDocument>['0']) => {
      return await PermitClientService.DeletePermitDocument(args);
    },
    onSuccess: () => refreshPermitting(queryClient),
  });
};

/**
 * The only way a permit changes stage (kalos-core #2791): the stage dialog, the Stage menu,
 * the board and Undo all save through it. A gate that isn't met is FailedPrecondition with
 * a message to show as it is.
 */
export const useAdvancePermitStageMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: Parameters<typeof AdvancePermitStageRequest.create>[0]) =>
      PermitClientService.AdvancePermitStage(AdvancePermitStageRequest.create(request)),
    onSuccess: () => refreshPermitting(queryClient),
  });
};

/** Uploads the documents picked on the new-request form to the request just created. */
export const useAttachPermitGroupDocumentsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      permitGroupId,
      documents,
    }: {
      permitGroupId: PermitGroup['id'];
      documents: File[];
    }) =>
      Promise.all(
        documents.map(async (file) =>
          PermitClientService.CreatePermitGroupDocument({
            fileData: new Uint8Array(await file.arrayBuffer()),
            fileName: file.name,
            permitGroupId,
          }),
        ),
      ),
    onSuccess: () => refreshPermitting(queryClient),
  });
};
